/**
 * Cost-ladder model registry. Stack validado via POC 2026-04-20 (14 passes, 90 renders).
 *
 * Pipeline 3-etapas (obrigatorio, sem tier fast):
 *   1. flux-kontext-max (prompt minimalista)    — US$ 0.080 — ~12s — estrutura/geometria
 *   2. brarchbr-lora (LoRA custom BR, 40 imgs)  — US$ 0.030 — ~8s  — DNA estetico BR
 *   3. topaz-image-upscale (Standard V2 2x)     — US$ 0.040 — ~14s — resolucao + crispness
 *   TOTAL: US$ 0.150 · ~35s wall-clock
 *
 * Comparacao Simplearchi (concorrente): flat FLUX Kontext ~US$ 0.05-0.08, sem LoRA BR,
 * sem upscale. Nossa qualidade superior em tons/textura + composicao cinematic 21:9.
 *
 * Tiers agora distinguem por ASPECT_RATIO e VARIANTS (nao por modelo):
 *   - draft      : 16:9 base, 1 variant            — 1 credito  (apenas etapa 1, SEM LoRA + upscale)
 *   - standard   : 16:9 full pipeline, 1 variant   — 3 creditos (kontext + lora + topaz)
 *   - premium    : 21:9 cinematic, 1 variant       — 3 creditos (cinematic wide)
 *   - variants   : 21:9 cinematic, 3 variants      — 8 creditos (3x full pipeline)
 */

export type TierId = 'draft' | 'standard' | 'premium' | 'variants';

export interface TierConfig {
  id: TierId;
  label: string;
  tagline: string;
  aspectRatio: '16:9' | '21:9';
  /** Numero de variantes geradas em paralelo. */
  numVariants: 1 | 3;
  /** Roda a pipeline completa (LoRA + upscale) ou so o kontext? */
  fullPipeline: boolean;
  /** Custo total estimado USD (todas etapas somadas) × num variants. */
  costPerRenderUsd: number;
  /** Creditos cobrados do user (margem ~3x custo). */
  creditsCharged: number;
  /** Est. wall-clock seconds. */
  latencyP50Sec: number;
}

/** Replicate model slugs com version hash (stack validado Pass 14). */
export const MODEL_SLUGS = {
  kontext: 'black-forest-labs/flux-kontext-max',
  brarchbr: 'joaocarlosleite-wq/brarchbr-lora:e5255352f1bb84fd5a386ac747453b2d5871ba4447c79e72c3d201ec843d91ac',
  topaz: 'topazlabs/image-upscale:2fdc3b86a01d338ae89ad58e5d9241398a8a01de9b0dda41ba8a0434c8a00dc3',
} as const;

export const TIERS: Record<TierId, TierConfig> = {
  draft: {
    id: 'draft',
    label: 'Draft',
    tagline: 'Rascunho rapido em resolucao media — ideal pra explorar variacoes antes de commitar.',
    aspectRatio: '16:9',
    numVariants: 1,
    fullPipeline: false,
    costPerRenderUsd: 0.08,
    creditsCharged: 1,
    latencyP50Sec: 12,
  },
  standard: {
    id: 'standard',
    label: 'Standard',
    tagline: 'Alta resolucao em 16:9. Default recomendado pra 95% dos casos.',
    aspectRatio: '16:9',
    numVariants: 1,
    fullPipeline: true,
    costPerRenderUsd: 0.15,
    creditsCharged: 3,
    latencyP50Sec: 35,
  },
  premium: {
    id: 'premium',
    label: 'Premium Wide',
    tagline: 'Alta resolucao em 21:9 cinematic — composicao editorial.',
    aspectRatio: '21:9',
    numVariants: 1,
    fullPipeline: true,
    costPerRenderUsd: 0.15,
    creditsCharged: 3,
    latencyP50Sec: 35,
  },
  variants: {
    id: 'variants',
    label: 'Variants x3',
    tagline: '3 versoes 21:9 cinematic em 1 clique. Cliente escolhe a melhor.',
    aspectRatio: '21:9',
    numVariants: 3,
    fullPipeline: true,
    costPerRenderUsd: 0.45,
    creditsCharged: 8,
    latencyP50Sec: 55,
  },
};

export const TIER_IDS: TierId[] = ['draft', 'standard', 'premium', 'variants'];

/** Build the Replicate input payload para etapa 1 (kontext_max). Server-only. */
export const buildKontextInput = (
  tier: TierConfig,
  imageUrl: string,
  instructionPrompt: string,
  opts: { seed?: number } = {},
): Record<string, unknown> => ({
  input_image: imageUrl,
  prompt: instructionPrompt,
  aspect_ratio: tier.aspectRatio,
  output_format: 'png',
  safety_tolerance: 2,
  prompt_upsampling: false,
  ...(opts.seed !== undefined ? { seed: opts.seed } : {}),
});

/** Build Replicate input para etapa 2 (BRARCHBR LoRA). */
export const buildBrarchbrInput = (
  tier: TierConfig,
  imageUrl: string,
  loraPrompt: string,
  opts: { seed?: number } = {},
): Record<string, unknown> => ({
  image: imageUrl,
  prompt: loraPrompt,
  lora_scale: 1.0,
  prompt_strength: 0.22,
  guidance_scale: 3.0,
  num_inference_steps: 30,
  aspect_ratio: tier.aspectRatio,
  output_format: 'png',
  output_quality: 95,
  ...(opts.seed !== undefined ? { seed: opts.seed } : {}),
});

/** Build Replicate input para etapa 3 (Topaz upscale). */
export const buildTopazInput = (imageUrl: string): Record<string, unknown> => ({
  image: imageUrl,
  enhance_model: 'Standard V2',
  upscale_factor: '2x',
  output_format: 'png',
  face_enhancement: false,
});
